/**
 * Accumulated time bands for the ScatterTimeseries brush.
 *
 * A plain drag replaces the selection; a Ctrl (or Cmd) drag ADDS a band to it. The bands are kept as a
 * list of ISO time ranges, unioned so overlapping or touching bands collapse into one — that keeps the
 * set small, sorted and non-overlapping no matter what order the user brushes in.
 */

export type ScatterBrushRange = {
  start_time: string;
  end_time: string;
};

const isUsableRange = (range: ScatterBrushRange | null | undefined): range is ScatterBrushRange =>
  Boolean(range?.start_time?.trim() && range?.end_time?.trim());

/** Union overlapping/adjacent bands so the accumulated set stays small, sorted and non-overlapping. */
export function mergeScatterBrushRanges(ranges: ScatterBrushRange[]): ScatterBrushRange[] {
  const usable = (Array.isArray(ranges) ? ranges : [])
    .filter(isUsableRange)
    .map((range) => ({
      start: new Date(range.start_time).getTime(),
      end: new Date(range.end_time).getTime(),
    }))
    .filter(({ start, end }) => Number.isFinite(start) && Number.isFinite(end))
    .sort((a, b) => Math.min(a.start, a.end) - Math.min(b.start, b.end));

  const merged: ScatterBrushRange[] = [];
  let current: { start: number; end: number } | null = null;
  for (const item of usable) {
    const start = Math.min(item.start, item.end);
    const end = Math.max(item.start, item.end);
    if (current && start <= current.end) {
      current.end = Math.max(current.end, end);
      continue;
    }
    if (current) {
      merged.push({
        start_time: new Date(current.start).toISOString(),
        end_time: new Date(current.end).toISOString(),
      });
    }
    current = { start, end };
  }
  if (current) {
    merged.push({
      start_time: new Date(current.start).toISOString(),
      end_time: new Date(current.end).toISOString(),
    });
  }

  return merged;
}

/** Ctrl+drag: keep the existing bands and add this one, merging any that overlap or touch. */
export function addScatterBrushRange(
  current: ScatterBrushRange[],
  range: ScatterBrushRange | null,
): ScatterBrushRange[] {
  const existing = Array.isArray(current) ? current : [];
  if (!isUsableRange(range)) {
    return mergeScatterBrushRanges(existing);
  }
  return mergeScatterBrushRanges([...existing, range]);
}
